import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { formatBrandMoney, getBrandCurrencySuffix, getBrandDatePickerLocale, getBrandLocale, resolveLiveVatRate } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Plus, Edit, Trash2, DollarSign, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, TrendingUp, Tag, Box, BarChart3, Columns3, ImagePlus } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { CustomNumberInput, CustomSelect } from '../../shared/components/custom';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import QawaemStatsCard from '../../products/saudi/components/QawaemStatsCard';
import QawaemTableToolbarSearch from '../../products/saudi/components/QawaemTableToolbarSearch';
import QawaemTableCardsViewToggle from '../../products/saudi/components/QawaemTableCardsViewToggle';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import { QawaemTableStickyActionsTd, mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import QawaemImportExportToolbar from '../../products/saudi/components/QawaemImportExportToolbar';
import QawaemTableEmptyRow from '../../shared/components/table/QawaemTableEmptyRow';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { QAWAEM_KPI_GRID_CLASS } from '../../products/saudi/components/QawaemErpListPageShell';
import { QAWAEM_MODAL_OVERLAY_CLASS } from '../../products/saudi/components/QawaemErpCrudModal';
import { facturationTableHeadClass, facturationTableScrollClass, facturationTableShellClass } from './components/FacturationListPageLayout';
import { articleFormLabelClass, ERP_MODAL_FORM_CLASS } from './components/articleFormFieldClasses';
import FacturationFormModalTabs from './components/FacturationFormModalTabs';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { AppConfirmDialog } from '../../shared/components/AppConfirmDialog';
import { useCrudDeleteConfirm } from '../../shared/hooks/useCrudDeleteConfirm';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { dispatchCatalogProductsRefresh } from '../articles/catalogProductOptions';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import { getBrandCountryOptionsWithLegacy, resolveCountryDisplayLabel } from '../../shared/utils/brandCountry';
import {
  CATALOG_UNITS_REFRESH_EVENT,
  fetchCatalogUnits,
  buildUnitSelectOptionsFromRecords,
  resolveUnitLabelFromOptions,
  collectOrphanUnitValues,
  defaultUnitFromOptions,
  resolveCatalogUnitLabelArabic,
  normalizeProductUnitCode,
  resolveProductUnitLabel,
} from '../articles/catalogUnitOptions';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import {
  buildCategorySelectOptions,
  categoryRecordMatchesRef,
  CATALOG_CATEGORIES_REFRESH_EVENT,
  filterCategoriesByType,
  findCategoryByRef,
  normalizeCategoriesResponse,
  resolveCategoryLabel,
} from '../articles/articleCatalogCategories';

registerLocale('fr', fr);
registerLocale('ar', arLocale);

const PRODUCTS_I18N = 'modules.article.pages.products';
const DISTRIBUTION_FILTER_KEYS = new Set(['active', 'inactive', 'low_stock', 'stock_ok', 'stock_critical']);
const MAX_PRODUCT_IMAGE_MB = 10;
const MAX_PRODUCT_IMAGE_BYTES = MAX_PRODUCT_IMAGE_MB * 1024 * 1024;

/** Empty numeric fields show placeholder instead of 0 in the product form. */
function formNumericFieldValue(raw) {
  if (raw === '' || raw == null) return '';
  const n = parseFloat(String(raw).replace(',', '.'));
  if (!Number.isFinite(n) || n === 0) return '';
  return String(raw).trim();
}

function isEmbeddedProductImage(url) {
  return typeof url === 'string' && url.startsWith('data:image');
}

// Inline edit cell for Products - MUST stay outside Products
function ProductEditableCell({
  recordId, field, value, className = 'text-white font-semibold',
  isEditing, editValue, onEditValueChange, onStartEdit, onSave, onCancel,
  readOnly = true,
  displayValue,
}) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try { el.setSelectionRange(len, len); } catch {}
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef} type="text" value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) { skipBlurSaveRef.current = false; return; }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); skipBlurSaveRef.current = true; onSave(recordId, field, e.currentTarget.value); }
          else if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }} autoComplete="off" spellCheck={false}
      />
    );
  }

  return (
    <span role="button" tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStartEdit(recordId, field, value); } }}
      className={`${className} cursor-pointer`} title={t(`${PRODUCTS_I18N}.click_to_edit`)}
    >{(displayValue ?? value) || '-'}</span>
  );
}

function ProductTablePhoto({ imageUrl, isLight = false, sizeClass = 'h-11 w-11', onPreview, previewLabel }) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !failed;
  const imageFrameClass = isLight ? 'border-gray-200 bg-gray-50' : 'border-white/15 bg-white/5';
  const placeholderClass = isLight ? 'border-gray-200 bg-gray-50' : 'border-white/20 bg-white/5';
  const iconClass = isLight ? 'text-gray-300' : 'text-white/30';

  if (showImage) {
    const img = (
      <img
        src={imageUrl}
        alt=""
        className={`${sizeClass} rounded-lg object-cover border ${imageFrameClass} shrink-0${onPreview ? ' transition-transform group-hover:scale-105' : ''}`}
        onError={() => setFailed(true)}
      />
    );

    if (onPreview) {
      return (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onPreview();
          }}
          className={`group relative rounded-lg shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70 ${sizeClass}`}
          title={previewLabel}
          aria-label={previewLabel}
        >
          {img}
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-black/0 opacity-0 transition-opacity group-hover:bg-black/25 group-hover:opacity-100">
            <Eye size={16} className="text-white drop-shadow-md" aria-hidden />
          </span>
        </button>
      );
    }

    return img;
  }

  return (
    <div
      className={`${sizeClass} rounded-lg border border-dashed ${placeholderClass} flex items-center justify-center shrink-0`}
      aria-hidden
    >
      <ImagePlus size={18} className={iconClass} />
    </div>
  );
}

const Products = () => {
  const { t, i18n } = useTranslation();
  const pt = useCallback((key, options) => t(`${PRODUCTS_I18N}.${key}`, options), [t]);
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Type dropdown
      const isClickOnTypeButton = event.target.closest('[data-product-type-dropdown-button]');
      const isClickInTypeDropdown = event.target.closest('[data-product-type-dropdown]');
      if (!isClickOnTypeButton && !isClickInTypeDropdown) {
        setShowTypeDropdown(false);
      }
      
      // Status dropdown
      const isClickOnStatusButton = event.target.closest('[data-product-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-product-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
      
      // Unit dropdown
      const isClickOnUnitButton = event.target.closest('[data-product-unit-dropdown-button]');
      const isClickInUnitDropdown = event.target.closest('[data-product-unit-dropdown]');
      if (!isClickOnUnitButton && !isClickInUnitDropdown) {
        setShowUnitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const { settings } = useSettings();
  const articlesConfig = useArticlesConfig();
  const liveVatRate = useMemo(
    () => resolveLiveVatRate(settings, articlesConfig),
    [settings?.taxes?.tva, settings?.facturation?.defaultTaxRate, articlesConfig?.defaultTaxRate],
  );
  const liveVatRateStr = String(liveVatRate);
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogProductUnits, setCatalogProductUnits] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [productFormTab, setProductFormTab] = useState('basic');
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const openProductImagePreview = useCallback((recordOrUrl, title = '') => {
    const url = typeof recordOrUrl === 'string' ? recordOrUrl : recordOrUrl?.image_url;
    if (!url) return;
    const resolvedTitle =
      title ||
      (typeof recordOrUrl === 'object'
        ? recordOrUrl.name_ar || recordOrUrl.name_en || recordOrUrl.name || recordOrUrl.reference
        : '');
    setImagePreview({ url, title: resolvedTitle });
  }, []);

  const closeProductImagePreview = useCallback(() => setImagePreview(null), []);

  useEffect(() => {
    if (!imagePreview) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeProductImagePreview();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [imagePreview, closeProductImagePreview]);

  // Inline editing states
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  const [formData, setFormData] = useState({ 
    name_en: '',
    name_ar: '',
    description: '', 
    reference: '',
    sku: '',
    barcode: '', 
    category: '',
    type: 'product',
    unit: '',
    price: '', 
    cost_price: '',
    tax_rate: liveVatRateStr,
    stock: '', 
    min_stock: '', 
    max_stock: '',
    status: 'active',
    image_url: '',
    country_of_origin: '',
    customs_classification: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_products');
  const [quickFilter, setQuickFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState(false);
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState(null);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);
  /** Panneau carte de la modale produit — pour garder le menu Unité à l’intérieur */
  const productModalPanelRef = useRef(null);
  const categoryFilterRef = useRef(null);
  const productImageInputRef = useRef(null);

  const loadProducts = async () => {
    try {
      const response = await localApiAdapter.get('/products?type=product');
      const rows = Array.isArray(response) ? response : (response?.data || []);
      setProducts(sortRecordsNewestFirst(Array.isArray(rows) ? rows.filter((p) => !p.type || p.type === 'product') : []));
      setApiUnavailable(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setProducts([]);
      const msg = String(error?.message || error);
      if (msg.includes('Failed to fetch') || error?.name === 'TypeError') {
        setApiUnavailable(true);
      }
    }
  };

  const loadCategories = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/categories?type=product');
      const productCategories = filterCategoriesByType(normalizeCategoriesResponse(response), 'product');
      setCategories(productCategories);
      setApiUnavailable(false);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      const msg = String(error?.message || error);
      if (msg.includes('Failed to fetch') || error?.name === 'TypeError') {
        setApiUnavailable(true);
      }
    }
  }, []);

  const loadCatalogProductUnits = useCallback(async () => {
    const rows = await fetchCatalogUnits('product');
    setCatalogProductUnits(rows);
  }, []);

  const orphanProductUnits = useMemo(
    () => collectOrphanUnitValues(products, catalogProductUnits),
    [products, catalogProductUnits],
  );

  const productUnitOptions = useMemo(
    () => buildUnitSelectOptionsFromRecords(catalogProductUnits, {
      orphanValues: orphanProductUnits,
      orphanSuffix: pt('units.not_in_uom', { defaultValue: '(غير مسجّل في وحدات القياس)' }),
      resolveOrphanLabel: (value) => resolveCatalogUnitLabelArabic(value, t),
    }),
    [catalogProductUnits, orphanProductUnits, pt, t],
  );

  const defaultProductUnit = useMemo(
    () => defaultUnitFromOptions(productUnitOptions, ''),
    [productUnitOptions],
  );

  const getCategoryName = useCallback(
    (categoryRef) => resolveCategoryLabel(categoryRef, categories, { emptyLabel: pt('placeholders.no_category') }),
    [categories, pt],
  );

  const productCategorySelectOptions = useMemo(
    () => buildCategorySelectOptions(categories, t, {
      includeClear: true,
      clearLabelKey: 'modules.article.pages.products.placeholders.no_category',
    }),
    [categories, t],
  );

  const getDistributionLabel = useCallback((filterKey) => {
    if (DISTRIBUTION_FILTER_KEYS.has(filterKey)) {
      return pt(`distribution.${filterKey}`);
    }
    return getCategoryName(filterKey);
  }, [pt, getCategoryName]);

  const getUnitLabel = useCallback(
    (unit) => resolveUnitLabelFromOptions(unit, productUnitOptions, t, {
      emptyLabel: pt('units.select'),
      fallbackResolver: (value) => resolveCatalogUnitLabelArabic(value, t),
    }),
    [productUnitOptions, t, pt],
  );

  const getCountryLabel = useCallback((countryCode) => {
    if (!countryCode) return pt('countries.select');
    return resolveCountryDisplayLabel(countryCode, (code) => {
      const key = `${PRODUCTS_I18N}.countries.${code}`;
      const translated = t(key);
      return translated !== key ? translated : code;
    });
  }, [pt, t]);

  const productCountrySelectOptions = useMemo(
    () => getBrandCountryOptionsWithLegacy(),
    [],
  );

  const getExportHeaders = useCallback(() => ([
    { key: 'reference', label: pt('columns.reference') },
    { key: 'sku', label: pt('columns.sku') },
    { key: 'name_en', label: pt('columns.name_en') },
    { key: 'name_ar', label: pt('columns.name_ar') },
    { key: 'name', label: pt('columns.name') },
    { key: 'description', label: pt('columns.description') },
    { key: 'barcode', label: pt('columns.barcode') },
    { key: 'category', label: pt('columns.category') },
    { key: 'unit', label: pt('columns.unit') },
    { key: 'price', label: pt('columns.price') },
    { key: 'cost_price', label: pt('columns.cost_price') },
    { key: 'tax_rate', label: pt('columns.tax_rate') },
    { key: 'stock', label: pt('columns.stock') },
    { key: 'status', label: pt('columns.status') },
    { key: 'country_of_origin', label: pt('columns.country_of_origin') },
    { key: 'customs_classification', label: pt('columns.customs_classification') },
    { key: 'image_url', label: pt('labels.image_url') },
  ]), [pt]);

  const getStatusLabel = useCallback((status) => (
    status === 'active' ? pt('status.active') : pt('status.inactive')
  ), [pt]);

  const computeNextProductReference = useCallback((productList = products) => {
    const prefix = getEntityPrefix(settings, 'product', 'PRD');
    return generateEntityReference(prefix, productList, (p) => p.reference || p.sku);
  }, [products, settings]);

  const openNewProductModal = () => {
    setEditingRecord(null);
    const nextRef = articlesConfig.autoGenerateProductReference !== false
      ? computeNextProductReference()
      : '';
    setFormData({
      name_en: '',
      name_ar: '',
      description: '',
      reference: nextRef,
      sku: '',
      barcode: '',
      category: '',
      type: 'product',
      unit: defaultProductUnit || articlesConfig.defaultUnit || '',
      price: '',
      cost_price: '',
      tax_rate: String(articlesConfig.defaultTaxRate ?? liveVatRate),
      stock: '',
      min_stock: '',
      max_stock: '',
      status: 'active',
      image_url: '',
      country_of_origin: '',
      customs_classification: '',
      notes: '',
    });
    setProductFormTab('basic');
    setShowImportDropdown(false);
    setShowExportDropdown(false);
    setShowModal(true);
  };

  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const {
    deleteConfirm,
    closeDeleteConfirm,
    openDeleteOne,
    openBulkDelete,
    takePendingIds,
  } = useCrudDeleteConfirm();

  useEffect(() => {
    void (async () => {
      await Promise.all([loadProducts(), loadCategories(), loadCatalogProductUnits()]);
    })();
  }, [loadCategories, loadCatalogProductUnits]);

  useEffect(() => {
    if (!showModal) return;
    void loadCategories();
    void loadCatalogProductUnits();
  }, [showModal, loadCategories, loadCatalogProductUnits]);

  useEffect(() => {
    const handleCategoriesRefresh = () => { void loadCategories(); };
    window.addEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
    window.addEventListener('refresh-all-data', handleCategoriesRefresh);
    return () => {
      window.removeEventListener(CATALOG_CATEGORIES_REFRESH_EVENT, handleCategoriesRefresh);
      window.removeEventListener('refresh-all-data', handleCategoriesRefresh);
    };
  }, [loadCategories]);

  useEffect(() => {
    const handleUnitsRefresh = () => { void loadCatalogProductUnits(); };
    window.addEventListener(CATALOG_UNITS_REFRESH_EVENT, handleUnitsRefresh);
    window.addEventListener('refresh-all-data', handleUnitsRefresh);
    return () => {
      window.removeEventListener(CATALOG_UNITS_REFRESH_EVENT, handleUnitsRefresh);
      window.removeEventListener('refresh-all-data', handleUnitsRefresh);
    };
  }, [loadCatalogProductUnits]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(event.target)) setIsCategoryFilterOpen(false);
      
      // Close status dropdown if clicking outside
      if (!event.target.closest('.relative')) {
        setOpenStatusDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    inactive: products.filter(p => p.status === 'inactive').length,
    lowStock: products.filter(p => parseFloat(p.stock) <= parseFloat(p.min_stock)).length,
    totalValue: products.reduce((sum, p) => sum + (parseFloat(p.price) || 0) * (parseFloat(p.stock) || 0), 0)
  };

  const mainTabs = useMemo(() => ([
    { id: 'all_products', name: pt('tabs.general'), icon: LayoutGrid },
    { id: 'par_categorie', name: pt('tabs.by_category'), icon: Tag },
    { id: 'par_stock', name: pt('tabs.by_stock'), icon: Box }
  ]), [pt]);

  const quickFilterButtons = useMemo(() => ([
    { id: 'all', name: pt('filters.all'), icon: Layers },
    { id: 'active', name: pt('filters.active'), icon: CheckCircle },
    { id: 'inactive', name: pt('filters.inactive'), icon: XCircle },
    { id: 'lowstock', name: pt('filters.low_stock'), icon: AlertCircle }
  ]), [pt]);

  const productsDistribution = useMemo(() => ({
    all_products: {
      title: pt('distribution.overview_title'),
      data: [
        { filterKey: 'active', count: stats.active, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'inactive', count: stats.inactive, color: "bg-gradient-to-r from-red-500 to-red-600" },
        { filterKey: 'low_stock', count: stats.lowStock, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" }
      ]
    },
    par_categorie: {
      title: pt('distribution.by_category_title'),
      data: categories.slice(0, 3).map(cat => ({
        filterKey: cat.id,
        count: products.filter(p => categoryRecordMatchesRef(p.category, cat.id, categories)).length,
        color: "bg-gradient-to-r from-blue-500 to-blue-600"
      }))
    },
    par_stock: {
      title: pt('distribution.by_stock_title'),
      data: [
        { filterKey: 'stock_ok', count: products.filter(p => parseFloat(p.stock) > parseFloat(p.min_stock)).length, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { filterKey: 'low_stock', count: stats.lowStock, color: "bg-gradient-to-r from-yellow-500 to-yellow-600" },
        { filterKey: 'stock_critical', count: products.filter(p => parseFloat(p.stock) === 0).length, color: "bg-gradient-to-r from-red-500 to-red-600" }
      ]
    }
  }), [categories, products, stats, pt]);

  const distributionInfo = productsDistribution[mainViewMode] || productsDistribution.all_products;

  const baseFilteredProducts = useMemo(
    () =>
      products.filter((p) => {
        if (dateRange.start || dateRange.end) {
          const productDate = new Date(p.created_at);

          if (dateRange.start) {
            const startOfDay = new Date(dateRange.start);
            startOfDay.setHours(0, 0, 0, 0);
            if (productDate < startOfDay) return false;
          }

          if (dateRange.end) {
            const endOfDay = new Date(dateRange.end);
            endOfDay.setHours(23, 59, 59, 999);
            if (productDate > endOfDay) return false;
          }
        }

        if (categoryFilter !== 'all' && !categoryRecordMatchesRef(p.category, categoryFilter, categories)) return false;

        if (
          searchTerm &&
          !(
            p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.barcode?.toLowerCase().includes(searchTerm.toLowerCase())
          )
        ) {
          return false;
        }

        if (mainViewMode === 'all_products') {
          if (quickFilter === 'active' && p.status !== 'active') return false;
          if (quickFilter === 'inactive' && p.status !== 'inactive') return false;
          if (quickFilter === 'lowstock' && parseFloat(p.stock) > parseFloat(p.min_stock)) return false;
        }

        if (activeDistributionFilter) {
          switch (mainViewMode) {
            case 'all_products':
              if (activeDistributionFilter === 'active' && p.status !== 'active') return false;
              if (activeDistributionFilter === 'inactive' && p.status !== 'inactive') return false;
              if (activeDistributionFilter === 'low_stock' && parseFloat(p.stock) > parseFloat(p.min_stock)) return false;
              break;
            case 'par_categorie':
              if (!categoryRecordMatchesRef(p.category, activeDistributionFilter, categories)) return false;
              break;
            case 'par_stock': {
              const stock = parseFloat(p.stock) || 0;
              const minStock = parseFloat(p.min_stock) || 0;
              if (activeDistributionFilter === 'stock_ok' && stock <= minStock) return false;
              if (activeDistributionFilter === 'low_stock' && (stock > minStock || stock === 0)) return false;
              if (activeDistributionFilter === 'stock_critical' && stock !== 0) return false;
              break;
            }
            default:
              break;
          }
        }

        return true;
      }),
    [products, dateRange, categoryFilter, searchTerm, mainViewMode, quickFilter, activeDistributionFilter, categories],
  );

  const productStatusFilterOptions = useMemo(() => ([
    { value: 'active', label: pt('status.active') },
    { value: 'inactive', label: pt('status.inactive') },
  ]), [pt]);

  const productUnitFilterOptions = useMemo(
    () => productUnitOptions.map(({ value, label }) => ({ value, label })),
    [productUnitOptions],
  );

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('reference', (r) => r.reference || r.sku || ''),
      textColumnRule('sku', (r) => r.sku || r.reference || ''),
      textColumnRule('name_ar', (r) => r.name_ar || r.name || ''),
      textColumnRule('name_en', (r) => r.name_en || r.name || ''),
      textColumnRule('category', (r) => getCategoryName(r.category)),
      textColumnRule('price', (r) => formatBrandMoney(r.price)),
      textColumnRule('stock', (r) => String(parseFloat(r.stock) || 0)),
      exactColumnRule('unit', (r) => normalizeProductUnitCode(r.unit, '')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [getCategoryName],
  );

  const filteredProducts = useMemo(
    () => applyColumnFilters(baseFilteredProducts, colFilters, columnFilterRules),
    [baseFilteredProducts, colFilters, columnFilterRules],
  );
  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredProducts);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const productsFooter = useMemo(() => {
    return filteredProducts.reduce(
      (acc, p) => {
        const st = parseFloat(p.stock) || 0;
        const pr = parseFloat(p.price) || 0;
        acc.stockTotal += st;
        acc.valeurStock += st * pr;
        return acc;
      },
      { stockTotal: 0, valeurStock: 0 }
    );
  }, [filteredProducts]);

  const handleProductImageFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert(pt('alerts.image_invalid_type', { defaultValue: 'يرجى اختيار ملف صورة (JPG, PNG, GIF, WebP)' }));
      return;
    }
    if (file.size > MAX_PRODUCT_IMAGE_BYTES) {
      notifyError(showNotify, pt('alerts.image_too_large', { maxMb: MAX_PRODUCT_IMAGE_MB, defaultValue: `حجم الصورة كبير جداً. الحد الأقصى ${MAX_PRODUCT_IMAGE_MB} ميجابايت.` }));
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({ ...prev, image_url: reader.result || '' }));
    };
    reader.onerror = () => {
      alert(pt('alerts.image_read_error', { defaultValue: 'تعذّر قراءة ملف الصورة' }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const nameEn = (formData.name_en || '').trim();
      const nameAr = (formData.name_ar || '').trim();
      if (!nameEn && !nameAr) {
        alert(pt('alerts.name_required'));
        return;
      }
      if (articlesConfig.requireBarcode && !(formData.barcode || '').trim()) {
        alert(pt('alerts.barcode_required', { defaultValue: 'الباركود إلزامي وفق إعدادات الوحدة.' }));
        return;
      }
      if (articlesConfig.requireCategory && (!formData.category || formData.category === '__clear__')) {
        alert(pt('alerts.category_required', { defaultValue: 'الفئة إلزامية وفق إعدادات الوحدة.' }));
        return;
      }
      if (!articlesConfig.allowDuplicateBarcode && (formData.barcode || '').trim()) {
        const barcodeTrim = formData.barcode.trim();
        const duplicate = products.find(
          (item) => item.barcode === barcodeTrim && item.id !== editingRecord?.id,
        );
        if (duplicate) {
          alert(pt('alerts.duplicate_barcode', { defaultValue: 'هذا الباركود مستخدم لمنتج آخر.' }));
          return;
        }
      }
      const refTrim = (formData.reference || '').trim();
      const reference = refTrim || (articlesConfig.autoGenerateProductReference !== false ? computeNextProductReference() : '');
      const skuTrim = (formData.sku || '').trim();
      const sku = skuTrim || reference;
      const submitData = { 
        name: nameEn || nameAr,
        name_en: nameEn || nameAr,
        name_ar: nameAr || null,
        description: formData.description || '',
        sku,
        reference,
        barcode: formData.barcode || '',
        category: formData.category === '__clear__' || !formData.category ? '' : formData.category,
        type: formData.type || 'product',
        unit: normalizeProductUnitCode(formData.unit, defaultProductUnit || ''),
        price: parseFloat(formData.price) || 0,
        cost_price: parseFloat(formData.cost_price) || 0,
        tax_rate: parseFloat(formData.tax_rate) || articlesConfig.defaultTaxRate || liveVatRate,
        stock: parseFloat(formData.stock) || 0,
        min_stock: parseFloat(formData.min_stock) || 0,
        max_stock: parseFloat(formData.max_stock) || 0,
        status: formData.status || 'active',
        image_url: formData.image_url || '',
        country_of_origin: formData.country_of_origin || '',
        customs_classification: formData.customs_classification || '',
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/products/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, pt('entity_label', { defaultValue: 'المنتج' }));
      } else {
        await localApiAdapter.post('/products', submitData);
        notifyCreated(showNotify, pt('entity_label', { defaultValue: 'المنتج' }));
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, pt('alerts.save_error', { message: error.message || pt('alerts.unknown_error') }));
      return;
    } finally {
      resetForm();
      loadProducts();
      dispatchCatalogProductsRefresh();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      name_en: record.name_en || record.name || '',
      name_ar: record.name_ar || '',
      description: record.description || '', 
      reference: record.reference || '',
      sku: record.sku || record.reference || '',
      barcode: record.barcode || '', 
      category: findCategoryByRef(categories, record.category)?.id || record.category || '',
      type: record.type || '',
      unit: normalizeProductUnitCode(record.unit, defaultProductUnit || ''),
      price: formNumericFieldValue(record.price),
      cost_price: formNumericFieldValue(record.cost_price),
      tax_rate: formNumericFieldValue(record.tax_rate) || (record.tax_rate?.toString() || liveVatRateStr),
      stock: formNumericFieldValue(record.stock),
      min_stock: formNumericFieldValue(record.min_stock),
      max_stock: formNumericFieldValue(record.max_stock),
      status: record.status || '',
      image_url: record.image_url || '',
      country_of_origin: record.country_of_origin || '',
      customs_classification: record.customs_classification || '',
      notes: record.notes || ''
    });
    setProductFormTab('basic');
    setShowImportDropdown(false);
    setShowExportDropdown(false);
    setShowModal(true);
  };
  
  const openDeleteProduct = (id) => {
    const record = products.find((p) => p.id === id);
    const label =
      record?.name_ar || record?.name_en || record?.name || record?.reference || String(id);
    openDeleteOne(id, label);
  };

  const openBulkDeleteProducts = () => {
    if (!selectedRecords.length) return;
    openBulkDelete(
      selectedRecords,
      pt('alerts.bulk_delete_confirm', { count: selectedRecords.length }),
    );
  };

  const executeDeleteProducts = async () => {
    const ids = takePendingIds();
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => localApiAdapter.delete(`/products/${id}`)));
      setSelectedRecords((prev) => prev.filter((id) => !ids.includes(id)));
      loadProducts();
      dispatchCatalogProductsRefresh();
      if (ids.length > 1) {
        notifyBulkDeleted(showNotify, ids.length, pt('entity_label_plural', { defaultValue: 'منتج' }));
      } else {
        notifyDeleted(showNotify, pt('entity_label', { defaultValue: 'المنتج' }));
      }
    } catch (error) {
      console.error('Error:', error);
      notifyError(
        showNotify,
        ids.length > 1
          ? pt('alerts.bulk_delete_error', { defaultValue: 'تعذّر حذف المنتجات المحددة' })
          : pt('alerts.delete_error', { defaultValue: 'تعذّر حذف المنتج' }),
      );
    }
  };

  // Export handlers
  const handleExportCSV = () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    exportToCSV(dataToExport, headers, `products_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const headers = getExportHeaders();
    
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    await exportToExcel(dataToExport, headers, `products_${new Date().toISOString().split('T')[0]}.xlsx`, pt('export.sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0 
        ? products.filter(p => selectedRecords.includes(p.id))
        : filteredProducts;

      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: pt('columns.reference'), value: (p) => p.reference },
          { label: pt('columns.name'), value: (p) => p.name },
          { label: pt('columns.category'), value: (p) => getCategoryName(p.category) },
          { label: pt('columns.unit'), value: (p) => getUnitLabel(p.unit) },
          {
            label: pt('columns.price'),
            align: 'right',
            value: (p) => formatBrandMoney(p.price || 0),
          },
          { label: pt('columns.stock'), align: 'right', value: (p) => String(p.stock ?? 0) },
          { label: pt('columns.status'), value: (p) => getStatusLabel(p.status) },
        ],
        listExportTitle: pt('export.list_title_upper'),
        clientTitle: pt('export.list_title'),
        sectionTitle: pt('export.section_title', { defaultValue: 'البيانات المُصدَّرة' }),
        printDocumentTitle: pt('export.document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
      if (error?.message === 'POPUP_BLOCKED') {
        alert(pt('alerts.pdf_popup_blocked', { defaultValue: 'يُرجى السماح بالنوافذ المنبثقة لهذا الموقع ثم إعادة تصدير PDF.' }));
      } else {
        alert(pt('alerts.pdf_export_error'));
      }
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportJSON = () => {
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    const jsonContent = JSON.stringify(dataToExport, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `products_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportXML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    let xmlContent = '<?xml version="1.0" encoding="UTF-8"?>\n<products>\n';
    dataToExport.forEach(item => {
      xmlContent += '  <product>\n';
      Object.keys(item).forEach(key => {
        const value = item[key] || '';
        xmlContent += `    <${key}>${value}</${key}>\n`;
      });
      xmlContent += '  </product>\n';
    });
    xmlContent += '</products>';
    
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `products_${new Date().toISOString().split('T')[0]}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportHTML = () => {
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    let htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${pt('export.html_title')}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h1 { color: #1e40af; }
    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
    th { background-color: #3b82f6; color: white; }
    tr:nth-child(even) { background-color: #f9fafb; }
  </style>
</head>
<body>
  <h1>${pt('export.html_title')}</h1>
  <p>${pt('export.date_label')} ${new Date().toLocaleDateString(dateLocale)}</p>
  <p>${pt('export.total_label', { count: dataToExport.length })}</p>
  <table>
    <thead>
      <tr>
        <th>${pt('columns.reference')}</th>
        <th>${pt('columns.name')}</th>
        <th>${pt('columns.category')}</th>
        <th>${pt('columns.price')}</th>
        <th>${pt('columns.stock')}</th>
        <th>${pt('columns.status')}</th>
      </tr>
    </thead>
    <tbody>`;
    
    dataToExport.forEach(p => {
      htmlContent += `
      <tr>
        <td>${p.reference || ''}</td>
        <td>${p.name || ''}</td>
        <td>${getCategoryName(p.category) || ''}</td>
        <td>${formatBrandMoney(p.price || 0)}</td>
        <td>${p.stock || 0}</td>
        <td>${getStatusLabel(p.status)}</td>
      </tr>`;
    });
    
    htmlContent += `
    </tbody>
  </table>
</body>
</html>`;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `products_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  const handleExportTXT = () => {
    const dataToExport = selectedRecords.length > 0 
      ? products.filter(p => selectedRecords.includes(p.id))
      : filteredProducts;
    
    let txtContent = `${pt('export.list_title_upper')}\n${pt('export.date_label')} ${new Date().toLocaleDateString(dateLocale)}\n${pt('export.total_label', { count: dataToExport.length })}\n\n`;
    txtContent += '='.repeat(100) + '\n\n';
    
    dataToExport.forEach(p => {
      txtContent += `${pt('columns.reference')}: ${p.reference || ''}\n`;
      txtContent += `${pt('columns.name')}: ${p.name || ''}\n`;
      txtContent += `${pt('columns.description')}: ${p.description || ''}\n`;
      txtContent += `${pt('columns.barcode')}: ${p.barcode || ''}\n`;
      txtContent += `${pt('columns.category')}: ${getCategoryName(p.category) || ''}\n`;
      txtContent += `${pt('columns.unit')}: ${getUnitLabel(p.unit) || ''}\n`;
      txtContent += `${pt('columns.price')}: ${formatBrandMoney(p.price || 0)}\n`;
      txtContent += `${pt('columns.stock')}: ${p.stock || 0}\n`;
      txtContent += `${pt('columns.status')}: ${getStatusLabel(p.status)}\n`;
      txtContent += '-'.repeat(100) + '\n\n';
    });
    
    const blob = new Blob([txtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `products_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowExportDropdown(false);
  };

  // Import handlers
  const handleImportCSV = () => {
    const headers = getExportHeaders();

    const schema = {
      requiredFields: ['name'],
      validators: {
        price: (value) => !value || !isNaN(parseFloat(value)),
        stock: (value) => !value || !isNaN(parseInt(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(pt('alerts.import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(pt('alerts.import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setProducts([...products, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(pt('alerts.import_file_error'));
      }
    }, '.csv');
  };

  const handleImportExcel = () => {
    const headers = getExportHeaders();

    const schema = {
      requiredFields: ['name'],
      validators: {
        price: (value) => !value || !isNaN(parseFloat(value)),
        stock: (value) => !value || !isNaN(parseInt(value))
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.errors.length > 0) {
          alert(pt('alerts.import_errors', { validCount: result.validCount, invalidCount: result.invalidCount, errors: result.errors.join('\n') }));
        } else {
          alert(pt('alerts.import_success', { count: result.validCount }));
        }
        
        if (result.data.length > 0) {
          setProducts([...products, ...result.data.map((item, idx) => ({
            ...item,
            id: Date.now() + idx
          }))]);
        }
        
        setShowImportDropdown(false);
      } catch (error) {
        console.error('Import error:', error);
        alert(pt('alerts.import_file_error'));
      }
    }, '.xlsx,.xls');
  };

  const handleImportJSON = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const data = JSON.parse(content);
        
        if (!Array.isArray(data)) {
          alert(pt('alerts.import_json_invalid'));
          return;
        }
        
        const importedProducts = data.map((item, idx) => ({
          ...item,
          id: Date.now() + idx
        }));
        
        setProducts([...products, ...importedProducts]);
        alert(pt('alerts.import_success', { count: importedProducts.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('JSON import error:', error);
        alert(pt('alerts.import_json_error'));
      }
    }, '.json');
  };

  const handleImportXML = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(content, 'text/xml');
        
        const productsNodes = xmlDoc.getElementsByTagName('product');
        const importedProducts = [];
        
        for (let i = 0; i < productsNodes.length; i++) {
          const node = productsNodes[i];
          const product = {
            id: Date.now() + i,
            reference: node.getElementsByTagName('reference')[0]?.textContent || '',
            name: node.getElementsByTagName('name')[0]?.textContent || '',
            description: node.getElementsByTagName('description')[0]?.textContent || '',
            barcode: node.getElementsByTagName('barcode')[0]?.textContent || '',
            category: node.getElementsByTagName('category')[0]?.textContent || '',
            unit: node.getElementsByTagName('unit')[0]?.textContent || '',
            price: parseFloat(node.getElementsByTagName('price')[0]?.textContent || '0'),
            cost_price: parseFloat(node.getElementsByTagName('cost_price')[0]?.textContent || '0'),
            tax_rate: parseFloat(node.getElementsByTagName('tax_rate')[0]?.textContent || liveVatRateStr),
            stock: parseInt(node.getElementsByTagName('stock')[0]?.textContent || '0'),
            status: node.getElementsByTagName('status')[0]?.textContent || 'active'
          };
          importedProducts.push(product);
        }
        
        setProducts([...products, ...importedProducts]);
        alert(pt('alerts.import_success', { count: importedProducts.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('XML import error:', error);
        alert(pt('alerts.import_xml_error'));
      }
    }, '.xml');
  };

  const handleImportTXT = () => {
    triggerFileInput(async (file) => {
      try {
        const content = await file.text();
        const lines = content.split('\n').filter(line => line.trim());
        
        const delimiter = lines[0].includes('\t') ? '\t' : ',';
        const headers = lines[0].split(delimiter).map(h => h.trim());
        
        const importedProducts = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(delimiter);
          const product = { id: Date.now() + i };
          
          headers.forEach((header, index) => {
            product[header.toLowerCase().replace(/\s+/g, '_')] = values[index]?.trim() || '';
          });
          
          importedProducts.push(product);
        }
        
        setProducts([...products, ...importedProducts]);
        alert(pt('alerts.import_success', { count: importedProducts.length }));
        setShowImportDropdown(false);
      } catch (error) {
        console.error('TXT import error:', error);
        alert(pt('alerts.import_txt_error'));
      }
    }, '.txt,.tsv');
  };

  const resetForm = () => {
    setFormData({
      name_en: '',
      name_ar: '',
      description: '',
      reference: '', 
      sku: '',
      barcode: '', 
      category: '',
      type: 'product',
      unit: '',
      price: '',
      cost_price: '',
      tax_rate: liveVatRateStr,
      stock: '',
      min_stock: '', 
      max_stock: '',
      status: 'active',
      image_url: '',
      country_of_origin: '',
      customs_classification: '',
      notes: ''
    });
    setEditingRecord(null);
    setProductFormTab('basic');
    setShowModal(false);
  };

  const filteredProductIds = useMemo(() => filteredProducts.map((p) => p.id), [filteredProducts]);
  const allRowsSelected = filteredProductIds.length > 0 && filteredProductIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredProductIds);
    }
  };
  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const statusOptions = useMemo(() => ([
    { value: 'active', label: pt('status.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: pt('status.inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' }
  ]), [pt]);

  /** Options icônes pour CustomSelect (facturation / archives) — le menu liste attend des nœuds React, pas une ref de composant. */
  const productStatusSelectOptions = useMemo(
    () => [
      { value: 'active', label: pt('status.active'), icon: <CheckCircle size={14} className="text-green-400" /> },
      { value: 'inactive', label: pt('status.inactive'), icon: <XCircle size={14} className="text-red-400" /> },
    ],
    [pt]
  );

  const productTypeSelectOptions = useMemo(
    () => [
      { value: 'product', label: pt('types.product') },
      { value: 'service', label: pt('types.service') },
    ],
    [pt]
  );

  const productMarginPreview = useMemo(() => {
    const price = parseFloat(formData.price);
    const cost = parseFloat(formData.cost_price);
    if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(cost)) return '';
    return `${(((price - cost) / price) * 100).toFixed(1)}%`;
  }, [formData.price, formData.cost_price]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/products/${recordId}`, { status: newStatus });
      setOpenStatusDropdown(null);
      setStatusDropdownPosition(null);
      await loadProducts();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  // Inline editing functions
  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = products.find(p => p.id === recordId);
    if (!record) { cancelEditing(); return; }

    const numericFields = new Set(['price', 'cost_price', 'tax_rate', 'stock', 'min_stock', 'max_stock']);
    if (numericFields.has(field)) {
      const raw = String(value ?? '').replace(/\s/g, '').replace(/\u00a0/g, '').replace(',', '.');
      const n = parseFloat(raw);
      if (Number.isNaN(n)) { cancelEditing(); return; }
      const orig = parseFloat(record[field]);
      const origN = Number.isNaN(orig) ? 0 : orig;
      if (n === origN) { cancelEditing(); return; }
      try {
        await localApiAdapter.put(`/products/${recordId}`, { [field]: n });
        await loadProducts();
        notifyUpdated(showNotify, pt('entity_label', { defaultValue: 'المنتج' }));
        cancelEditing();
        setViewingRecord((prev) => (prev && prev.id === recordId ? { ...prev, [field]: n } : prev));
      } catch (error) {
        console.error('Error saving inline edit:', error);
        cancelEditing();
      }
      return;
    }

    const norm = (v) => String(v ?? '').trim();
    const newVal = norm(value);
    const originalVal = norm(record[field]);
    if (newVal === originalVal) {
      cancelEditing();
      return;
    }
    if (field === 'name_en' || field === 'name_ar') {
      if (!newVal && !(field === 'name_en' ? norm(record.name_ar) : norm(record.name_en))) {
        cancelEditing();
        return;
      }
    } else if (field === 'name' && !newVal) {
      cancelEditing();
      return;
    }

    try {
      const payload = { [field]: newVal };
      if (field === 'name_en' || field === 'name_ar') {
        const nextEn = field === 'name_en' ? newVal : norm(record.name_en);
        const nextAr = field === 'name_ar' ? newVal : norm(record.name_ar);
        payload.name = nextEn || nextAr || norm(record.name);
        payload.name_en = nextEn || payload.name;
        payload.name_ar = nextAr;
      }
      await localApiAdapter.put(`/products/${recordId}`, payload);
      await loadProducts();
      notifyUpdated(showNotify, pt('entity_label', { defaultValue: 'المنتج' }));
      cancelEditing();
      setViewingRecord((prev) => {
        if (!prev || prev.id !== recordId) return prev;
        if (field === 'name_en' || field === 'name_ar') {
          const nextEn = field === 'name_en' ? newVal : norm(record.name_en);
          const nextAr = field === 'name_ar' ? newVal : norm(record.name_ar);
          return {
            ...prev,
            [field]: newVal,
            name_en: nextEn || prev.name,
            name_ar: nextAr,
            name: nextEn || nextAr || prev.name,
          };
        }
        return { ...prev, [field]: newVal };
      });
    } catch (error) {
      console.error('Error saving inline edit:', error);
      cancelEditing();
    }
  };

  // Simple non-clickable status badge
  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };

  // Clickable status badge component (proper React component with hooks)
  const ClickableStatusBadge = ({ status, recordId }) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    const buttonRef = React.useRef(null);

    const handleToggleDropdown = (e) => {
      e.stopPropagation();
      
      if (openStatusDropdown === recordId) {
        // Close dropdown
        setOpenStatusDropdown(null);
        setStatusDropdownPosition(null);
      } else {
        // Calculate position and update both states together
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const dropdownWidth = 200; // min-w-[200px]
          const dropdownHeight = 100; // approximate height
          
          // Calculate initial position (centered)
          let left = rect.left + (rect.width / 2);
          let top = rect.bottom + 8;
          
          // Check right edge
          if (left + (dropdownWidth / 2) > window.innerWidth - 20) {
            left = window.innerWidth - dropdownWidth - 20;
          }
          
          // Check left edge
          if (left - (dropdownWidth / 2) < 20) {
            left = dropdownWidth / 2 + 20;
          }
          
          // Check bottom edge
          if (top + dropdownHeight > window.innerHeight - 20) {
            top = rect.top - dropdownHeight - 8; // Show above instead
          }
          
          const newPosition = { top, left };
          setStatusDropdownPosition(newPosition);
          setOpenStatusDropdown(recordId);
        }
      }
    };

  return (
      <div className="relative inline-block">
          <button
          ref={buttonRef}
          onClick={handleToggleDropdown}
          className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style} hover:opacity-80 transition-all cursor-pointer`}
          title={pt('click_change_status')}
        >
          <Icon size={14} />{label}<ChevronDown size={12} className={`transition-transform ${openStatusDropdown === recordId ? 'rotate-180' : ''}`} />
          </button>
        {openStatusDropdown === recordId && statusDropdownPosition && ReactDOM.createPortal(
          <div 
            className="border border-blue-400/60 rounded-2xl shadow-2xl overflow-hidden min-w-[200px] transform -translate-x-1/2" 
            style={{ position: 'fixed', zIndex: 99999, top: `${statusDropdownPosition.top}px`, left: `${statusDropdownPosition.left}px`, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
          >
            {statusOptions.map(option => {
              const OptionIcon = option.icon;
              return (
          <button 
                  key={option.value}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStatusChange(recordId, option.value);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-500/20 transition-all text-left ${option.value === status ? 'bg-blue-500/25 shadow-inner' : ''}`}
                >
                  <OptionIcon size={16} className={option.style.split(' ')[1].replace('text-', 'text-')} />
                  <span className="text-white text-sm font-medium">{option.label}</span>
                  {option.value === status && <Check size={14} className="ml-auto text-blue-400" />}
          </button>
              );
            })}
          </div>,
          document.body
        )}
      </div>
    );
  };

  const qawaemBaseColumns = useMemo(
    () => [
      { key: 'reference', label: pt('columns.reference'), required: true },
      { key: 'sku', label: pt('columns.sku'), required: true },
      { key: 'name_ar', label: pt('columns.name_ar'), required: true },
      { key: 'name_en', label: pt('columns.name_en'), required: true },
      { key: 'category', label: pt('columns.category') },
      { key: 'price', label: pt('columns.price') },
      { key: 'stock', label: pt('columns.stock') },
      { key: 'unit', label: pt('columns.unit') },
      { key: 'status', label: pt('columns.status') },
    ],
    [pt],
  );
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemProductColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, pt('columns.actions')),
    [pickerColumns, pt],
  );
  const productsTableColSpan = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 1 }),
    [qawaemBaseColumns, showCol],
  );

  const productFormTabs = useMemo(() => ([
    { id: 'basic', label: pt('sections.basic_info'), icon: Package, iconColor: 'text-blue-400' },
    { id: 'details', label: pt('sections.notes_other'), icon: Tag, iconColor: 'text-purple-400' },
    { id: 'pricing_stock', label: pt('sections.pricing_and_stock'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [pt]);

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div className="flex-1 min-w-0">
          {qawaem ? (
            <>
              <QawaemPageTitleWithFilters
                titleSuffix={<QawaemTableCardsViewToggle value={viewMode} onChange={setViewMode} />}
                filters={
                  <>
                    {selectedRecords.length > 0 ? (
                      <button
                        type="button"
                        onClick={openBulkDeleteProducts}
                        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                        title={pt('delete_selection')}
                      >
                        <Trash2 size={16} />
                        <span>{pt('delete_with_count', { count: selectedRecords.length })}</span>
                      </button>
                    ) : null}
                    <QawaemTableToolbarSearch
                      viewMode={viewMode}
                      value={searchTerm}
                      onChange={setSearchTerm}
                      placeholder={pt('search_placeholder')}
                    />
                  </>
                }
              >
                <h1 className="text-3xl font-bold text-forest-900 flex items-center gap-3">
                  <Package className="page-title-icon page-title-icon--bronze h-8 w-8" />
                  {pt('title')}
                </h1>
              </QawaemPageTitleWithFilters>
              <p className="page-section-desc">{pt('subtitle')}</p>
              {apiUnavailable ? (
                <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
                  {pt('api_unavailable', {
                    defaultValue: 'تعذّر الاتصال بالخادم المحلي. شغّل الحزمة الكاملة: npm run dev',
                  })}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Package className="h-8 w-8 text-blue-400" />
                {pt('title')}
              </h1>
              <p className="text-white/60 mt-2">{pt('subtitle')}</p>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!qawaem ? (
            <>
              <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
                <Filter className="h-5 w-5" /><span className="hidden sm:inline">{pt('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={openNewProductModal}>
                <Plus className="h-5 w-5" /><span>{pt('new_product')}</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn-stat-green btn-header-compact shrink-0" onClick={openNewProductModal}>
                <span className="hidden sm:inline">{pt('new_product')}</span>
                <Plus className="h-4 w-4" />
              </button>
              <QawaemImportExportToolbar
                compact
                importOpen={showImportDropdown}
                exportOpen={showExportDropdown}
                onToggleImport={() => {
                  setShowImportDropdown(!showImportDropdown);
                  setShowExportDropdown(false);
                }}
                onToggleExport={() => {
                  setShowExportDropdown(!showExportDropdown);
                  setShowImportDropdown(false);
                }}
                importRef={importDropdownRef}
                exportRef={exportDropdownRef}
                importLabel={pt('import')}
                exportLabel={t('common.export')}
                generatingLabel={pt('generating')}
                isGeneratingPDF={isGeneratingPDF}
                onImportCSV={handleImportCSV}
                onImportExcel={handleImportExcel}
                onExportPDF={handleExportPDF}
                onExportExcel={handleExportExcel}
                onExportCSV={handleExportCSV}
              />
            </>
          )}
        </div>
      </div>

      {qawaem ? (
        <div className={QAWAEM_KPI_GRID_CLASS}>
          <QawaemStatsCard label={pt('stats.total')} value={stats.total} icon={Package} variant="copper" delay={0} />
          <QawaemStatsCard label={pt('stats.active')} value={stats.active} icon={CheckCircle} variant="forest" delay={0.08} />
          <QawaemStatsCard label={pt('stats.inactive')} value={stats.inactive} icon={XCircle} variant="copper" delay={0.15} />
          <QawaemStatsCard label={pt('stats.low_stock')} value={stats.lowStock} icon={AlertCircle} variant="forest" delay={0.22} />
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { title: pt('stats.total'), value: stats.total, icon: Package, color: 'from-blue-500 to-blue-600' },
          { title: pt('stats.active'), value: stats.active, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: pt('stats.inactive'), value: stats.inactive, icon: XCircle, color: 'from-red-500 to-red-600' },
          { title: pt('stats.low_stock'), value: stats.lowStock, icon: AlertCircle, color: 'from-amber-500 to-amber-600' },
        ].map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{typeof stat.value === 'number' ? stat.value : stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      )}

      {!qawaem ? (
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-6">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto table-scroll">
                  {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                    </button>
                  ))}
                </div>
              </div>
              {mainViewMode === 'all_products' && (
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                  <div className="flex space-x-1 overflow-x-auto table-scroll">
                    {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
                  {activeDistributionFilter && (
                    <button 
                      onClick={() => setActiveDistributionFilter(null)}
                      className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                    >
                      <XCircle size={14} />
                      {pt('distribution.reset_filter')}
                    </button>
                  )}
                </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>
                  {distributionInfo.data.map((item) => (
                    <button 
                      key={item.filterKey}
                      onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={pt('distribution.click_filter', { name: getDistributionLabel(item.filterKey) })}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{getDistributionLabel(item.filterKey)}</h4>
                        <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                      </div>
                      <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                            const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                            return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                      </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                          const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                          const percent = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                          return pt('distribution.percent_of_products', { percent });
                  })()}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
      ) : null}

      <div className={qawaem ? 'glass-panel glass-table-shell overflow-visible' : 'bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-visible'}>
        {!qawaem ? (
        <div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {shouldShowCardsToolbarSearch(viewMode) ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={pt('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              
              {/* View Mode Toggle - FIRST ELEMENT */}
            <FacturationViewModeToggle
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLight={isDashboardLightMode}
              cardsTitle={pt('view_cards')}
              tableTitle={pt('view_table')}
            />

            <div className="relative" ref={dateRangeRef}>
              <button
                type="button"
                onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
                title={pt('filter_period')}
                aria-label={pt('filter_period')}
                className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
              >
                <Calendar size={18} className="text-white/80 shrink-0" />
                <span className="hidden sm:inline text-white font-medium text-sm">{pt('period')}</span>
                <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
              </button>
              {isDateRangeOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}
                  >
                    <div className="p-4" style={{ overflow: 'visible' }}>
                      <div className="flex flex-col gap-3" style={{ overflow: 'visible' }}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ overflow: 'visible' }}>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{pt('start_date')}</label>
                            <DatePicker
                              selected={dateRange.start}
                              onChange={(date) => setDateRange({...dateRange, start: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={pt('select')}
                              locale={qawaem ? 'ar' : 'fr'}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                            />
                          </div>
                          <div style={{ overflow: 'visible' }}>
                            <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{pt('end_date')}</label>
                            <DatePicker
                              selected={dateRange.end}
                              onChange={(date) => setDateRange({...dateRange, end: date})}
                              dateFormat="dd/MM/yyyy"
                              placeholderText={pt('select')}
                              minDate={dateRange.start}
                              locale={qawaem ? 'ar' : 'fr'}
                              className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-blue-400/60 backdrop-blur-sm gps-datepicker"
                              calendarClassName="gps-calendar"
                              wrapperClassName="w-full"
                              portalId="qawaem-datepicker-portal"
                              popperClassName="date-picker-popper-high-z"
                              popperProps={{
                                strategy: 'fixed'
                              }}
                            />
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2 border-t border-blue-400/30">
                          <button onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-blue-500/20 text-white/80 hover:text-white rounded-xl text-sm transition-all font-medium">{pt('reset')}</button>
                          <button onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl text-sm transition-all font-medium shadow-lg shadow-blue-500/30">{pt('apply')}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Category Filter Dropdown */}
              <div className="relative" ref={categoryFilterRef}>
                <button 
                  type="button"
                  onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)} 
                  title={categoryFilter === 'all' ? pt('filter_by_category') : pt('category_label', { name: getCategoryName(categoryFilter) })}
                  aria-label={categoryFilter === 'all' ? pt('filter_by_category') : pt('category_label', { name: getCategoryName(categoryFilter) })}
                  className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5"
                >
                  <Tag size={18} className="text-white/80 shrink-0" />
                  <span className="hidden sm:inline text-white font-medium text-sm">
                    {categoryFilter === 'all' ? pt('category') : getCategoryName(categoryFilter)}
                  </span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 text-white/70 transition-transform ${isCategoryFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isCategoryFilterOpen && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 99999, 
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2 max-h-64 overflow-y-auto">
                      <button
                        onClick={() => {
                          setCategoryFilter('all');
                          setIsCategoryFilterOpen(false);
                        }}
                        className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${categoryFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                      >
                        <div className="w-2 h-2 rounded-full bg-white/50"></div>
                        <span className="text-sm font-medium">{pt('all_categories')}</span>
                      </button>
                      {categories.map(cat => (
                      <button
                          key={cat.id}
                          onClick={() => {
                            setCategoryFilter(cat.id);
                            setIsCategoryFilterOpen(false);
                          }}
                          className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${categoryFilter === cat.id ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}
                        >
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color || '#3b82f6' }}></div>
                          <span className="text-sm font-medium">{cat.name}</span>
                      </button>
                      ))}
                  </div>
                </div>
              )}
            </div>

              {selectedRecords.length > 0 && (
                <button
                  onClick={openBulkDeleteProducts}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium"
                  title={pt('delete_selection')}
                >
                  <Trash2 size={16} />
                  <span className="hidden sm:inline">{pt('delete_with_count', { count: selectedRecords.length })}</span>
                </button>
              )}

              {/* Import Dropdown - RED */}
              <div className="relative" style={{ zIndex: 30 }} ref={importDropdownRef}>
                    <button
                      type="button"
                      onClick={() => {
                    setShowImportDropdown(!showImportDropdown);
                    setShowExportDropdown(false);
                      }}
                      title={pt('import_aria')}
                      aria-label={pt('import_aria')}
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                    >
                  <Upload size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{pt('import')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                    </button>
                {showImportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 40, overflow: 'visible', backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}
                  >
                    <div className="p-2">
                    <button onClick={handleImportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                    <button onClick={handleImportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                    <button onClick={handleImportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                    <button onClick={handleImportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                    <button onClick={handleImportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">TXT/TSV</span></button>
                    </div>
                  </div>
                )}
              </div>

              {/* Export Dropdown - GREEN */}
              <div className="relative" style={{ zIndex: 30 }} ref={exportDropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    setShowExportDropdown(!showExportDropdown);
                    setShowImportDropdown(false);
                  }}
                  title={pt('export_aria')}
                  aria-label={pt('export_aria')}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download size={16} className="shrink-0" />
                  <span className="hidden sm:inline text-sm font-medium">{t('common.export')}</span>
                  <ChevronDown size={14} className={`hidden sm:block shrink-0 transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div 
                    className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px]" 
                    style={{ 
                      position: 'absolute', 
                      top: 'calc(100% + 8px)', 
                      right: 0, 
                      zIndex: 40,
                      overflow: 'visible',
                      backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))',
                      boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' 
                    }}
                  >
                    <div className="p-2">
                    <button onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl disabled:opacity-50"><FileText size={16} className="text-blue-400" /><span className="text-sm font-medium">{isGeneratingPDF ? pt('generating') : t('common.format_pdf')}</span></button>
                    <button onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{t('common.format_excel')}</span></button>
                    <button onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{t('common.format_csv')}</span></button>
                    <button onClick={handleExportJSON} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-yellow-400" /><span className="text-sm font-medium">JSON</span></button>
                    <button onClick={handleExportXML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-orange-400" /><span className="text-sm font-medium">XML</span></button>
                    <button onClick={handleExportHTML} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-pink-400" /><span className="text-sm font-medium">HTML</span></button>
                    <button onClick={handleExportTXT} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl"><FileText size={16} className="text-gray-400" /><span className="text-sm font-medium">{t('common.format_txt')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        ) : null}

        {/* Table View */}
        {viewMode === 'table' && (
          <div className={qawaem ? 'glass-table-scroll overflow-x-auto' : 'overflow-x-auto rounded-b-3xl'}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                        title={allRowsSelected ? pt('deselect_all', { defaultValue: 'إلغاء تحديد الكل' }) : pt('select_all', { defaultValue: 'تحديد الكل' })}
                      />
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center align-middle text-white font-semibold text-sm whitespace-nowrap w-[72px]">
                    {pt('columns.image', { defaultValue: 'الصورة' })}
                  </th>
                  
                  {showCol('reference') ? (
                  <TableColumnHeaderFilter columnKey="reference" label={pt('columns.reference')} value={colFilters.reference ?? ''} onChange={(v) => setColFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('sku') ? (
                  <TableColumnHeaderFilter columnKey="sku" label={pt('columns.sku')} value={colFilters.sku ?? ''} onChange={(v) => setColFilter('sku', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('name_ar') ? (
                  <TableColumnHeaderFilter columnKey="name_ar" label={pt('columns.name_ar')} value={colFilters.name_ar ?? ''} onChange={(v) => setColFilter('name_ar', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('name_en') ? (
                  <TableColumnHeaderFilter columnKey="name_en" label={pt('columns.name_en')} value={colFilters.name_en ?? ''} onChange={(v) => setColFilter('name_en', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('category') ? (
                  <TableColumnHeaderFilter columnKey="category" label={pt('columns.category')} value={colFilters.category ?? ''} onChange={(v) => setColFilter('category', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('price') ? (
                  <TableColumnHeaderFilter columnKey="price" label={pt('columns.price')} value={colFilters.price ?? ''} onChange={(v) => setColFilter('price', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('stock') ? (
                  <TableColumnHeaderFilter columnKey="stock" label={pt('columns.stock')} value={colFilters.stock ?? ''} onChange={(v) => setColFilter('stock', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('unit') ? (
                  <TableColumnHeaderFilter columnKey="unit" label={pt('columns.unit')} kind="select" options={productUnitFilterOptions} value={colFilters.unit ?? ''} onChange={(v) => setColFilter('unit', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {showCol('status') ? (
                  <TableColumnHeaderFilter columnKey="status" label={pt('columns.status')} kind="select" options={productStatusFilterOptions} value={colFilters.status ?? ''} onChange={(v) => setColFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={pt('columns.actions')}
                    columns={qawaemProductColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemProductColumns.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredProducts.length > 0 ? (
                  pagedItems.map((record, index) => {
                    // Helper function to get product-adaptive light text
                    const getAdaptiveLightText = () => {
                      const stock = parseFloat(record.stock) || 0;
                      const minStock = parseFloat(record.min_stock) || 0;
                      if (stock === 0) return pt('stock.depleted');
                      if (stock <= minStock) return pt('stock.low', { stock });
                      return pt('stock.available');
                    };

                    return (
                      <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''} ${index === 0 ? 'first:rounded-t-3xl' : ''} ${index === filteredProducts.length - 1 ? 'last:rounded-b-3xl' : ''}`}>
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                        <td className="px-3 py-4 text-center align-middle">
                          <div className="flex justify-center">
                            <ProductTablePhoto
                              imageUrl={record.image_url}
                              isLight={isDashboardLightMode}
                              onPreview={() => openProductImagePreview(record)}
                              previewLabel={pt('actions.view_image', { defaultValue: 'عرض الصورة بالكامل' })}
                            />
                          </div>
                        </td>
                        
                        {/* Référence / N° Produit */}
                        {showCol('reference') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <ProductEditableCell recordId={record.id} field="reference" value={record.reference} className="text-white font-semibold" readOnly
                              isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                            <ProductEditableCell recordId={record.id} field="barcode" value={record.barcode} className="text-white/50 text-xs mt-0.5" readOnly
                              isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                          </div>
                        </td>
                        ) : null}

                        {showCol('sku') ? (
                        <td className="px-6 py-4">
                          <ProductEditableCell recordId={record.id} field="sku" value={record.sku || record.reference || ''} className="text-white font-mono font-semibold" readOnly
                            isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </td>
                        ) : null}

                        {showCol('name_ar') ? (
                        <td className="px-6 py-4">
                          <ProductEditableCell recordId={record.id} field="name_ar" value={record.name_ar || record.name || ''} className="text-white font-semibold" readOnly
                            isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </td>
                        ) : null}

                        {showCol('name_en') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <ProductEditableCell recordId={record.id} field="name_en" value={record.name_en || record.name || ''} className="text-white font-semibold" readOnly
                              isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                            <ProductEditableCell recordId={record.id} field="description" value={record.description} className="text-white/50 text-xs mt-0.5" readOnly
                              isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                            <span className="text-white/45 text-[11px] mt-0.5">{getAdaptiveLightText()}</span>
                          </div>
                        </td>
                        ) : null}

                        {/* Catégorie - Category only */}
                        {showCol('category') ? (
                        <td className="px-6 py-4 text-start align-middle">
                          <span className="text-white font-semibold">{getCategoryName(record.category)}</span>
                        </td>
                        ) : null}
                        
                        {/* Prix - Price bold, purchase price light */}
                        {showCol('price') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <ProductEditableCell recordId={record.id} field="price" value={record.price != null && record.price !== '' ? String(record.price) : ''} className="text-white font-semibold" readOnly
                              isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                            />
                            <span className="text-white/50 text-xs mt-0.5">{pt('stock.purchase_prefix')}&nbsp;
                              <ProductEditableCell recordId={record.id} field="cost_price" value={record.cost_price != null && record.cost_price !== '' ? String(record.cost_price) : ''} className="text-white/50 text-xs inline" readOnly
                                isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                              />
                            </span>
                          </div>
                      </td>
                        ) : null}
                        
                        {/* Stock - Stock bold only */}
                        {showCol('stock') ? (
                        <td className="px-6 py-4">
                          <ProductEditableCell recordId={record.id} field="stock" value={record.stock != null && record.stock !== '' ? String(record.stock) : ''} className={`font-semibold ${parseFloat(record.stock) === 0 ? 'text-red-400' : parseFloat(record.stock) <= parseFloat(record.min_stock) ? 'text-yellow-400' : 'text-green-400'}`} readOnly
                            isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                      </td>
                        ) : null}
                        
                        {/* Unité */}
                        {showCol('unit') ? (
                        <td className="px-6 py-4 text-start align-middle">
                          <ProductEditableCell recordId={record.id} field="unit" value={record.unit || ''} displayValue={getUnitLabel(record.unit)} className="text-white" readOnly
                            isEditing={false} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                          />
                        </td>
                        ) : null}
                        
                        {showCol('status') ? (
                        <td className="px-6 py-4 text-center align-middle"><div className="flex justify-center"><ClickableStatusBadge status={record.status} recordId={record.id} /></div></td>
                        ) : null}
                        <QawaemTableStickyActionsTd>
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => openDeleteProduct(record.id)}
                            viewTitle={pt('actions.view')}
                            editTitle={pt('actions.edit')}
                            deleteTitle={pt('actions.delete')}
                          />
                        </QawaemTableStickyActionsTd>
                      </tr>
                    );
                  })
                ) : (
                  <QawaemTableEmptyRow
                    colSpan={productsTableColSpan}
                    icon={Package}
                    message={pt('empty.no_products')}
                    iconClassName="h-12 w-12 text-white/30 mb-4 mx-auto"
                    messageClassName="text-lg text-white/60"
                  />
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={productsTableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{pt('footer.products_total', { count: filteredProducts.length })}</span>
                      <span>{pt('footer.total_stock')} <span className="text-white font-semibold">{productsFooter.stockTotal.toLocaleString(dateLocale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span></span>
                      <span>{pt('footer.stock_value')} <span className="text-white font-semibold">{formatBrandMoney(productsFooter.valeurStock)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Card View — compact 2×2 sections, fixed layout */}
        {viewMode === 'cards' && (
          <div className="p-4 sm:p-5 grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 rounded-b-3xl qawaem-product-cards-grid">
            {filteredProducts.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.04 }}
                  className="qawaem-product-card flex flex-col min-w-0 rounded-2xl border border-white/20 bg-white/[0.03] overflow-hidden transition-all hover:border-white/40 hover:bg-white/[0.05]"
                >
                  {/* Header: photo + title + meta + actions */}
                  <div className="flex items-start gap-3 p-4 pb-3 border-b border-white/10">
                    <ProductTablePhoto
                      imageUrl={record.image_url}
                      isLight={isDashboardLightMode}
                      sizeClass="h-11 w-11 shrink-0"
                      onPreview={() => openProductImagePreview(record)}
                      previewLabel={pt('actions.view_image', { defaultValue: 'عرض الصورة بالكامل' })}
                    />
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <ClickableStatusBadge status={record.status} recordId={record.id} />
                        {record.created_at ? (
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/15 text-white/80">
                            {new Date(record.created_at).toLocaleDateString(dateLocale)}
                          </span>
                        ) : null}
                      </div>
                      {record.name_ar ? (
                        <ProductEditableCell
                          recordId={record.id}
                          field="name_ar"
                          value={record.name_ar}
                          className="text-base font-bold text-white break-words block"
                          isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name_ar'}
                          editValue={editValue}
                          onEditValueChange={setEditValue}
                          onStartEdit={startEditing}
                          onSave={saveInlineEdit}
                          onCancel={cancelEditing}
                        />
                      ) : null}
                      <ProductEditableCell
                        recordId={record.id}
                        field="name_en"
                        value={record.name_en || record.name || ''}
                        className={`${record.name_ar ? 'text-sm font-medium text-white/80' : 'text-base font-bold text-white'} break-words block`}
                        isEditing={editingCell?.recordId === record.id && editingCell?.field === 'name_en'}
                        editValue={editValue}
                        onEditValueChange={setEditValue}
                        onStartEdit={startEditing}
                        onSave={saveInlineEdit}
                        onCancel={cancelEditing}
                      />
                    </div>
                    <div className="shrink-0">
                      <QawaemErpRowActions
                        onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => openDeleteProduct(record.id)}
                        viewTitle={pt('actions.view')}
                        editTitle={pt('actions.edit')}
                        deleteTitle={pt('actions.delete')}
                        compact
                      />
                    </div>
                  </div>

                  {/* Body: 2×2 balanced grid — no orphan row */}
                  <div className="p-4 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    <section className="rounded-xl border border-white/10 bg-white/[0.04] p-3 min-h-0">
                      <h5 className="text-xs font-semibold text-white/90 mb-2 pb-1 border-b border-white/10">
                        {pt('sections.basic_info')}
                      </h5>
                      <div className="space-y-1.5 text-sm text-white/70">
                        <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                          <span className="font-medium shrink-0">{pt('labels.product_number_colon')}</span>
                          <ProductEditableCell
                            recordId={record.id}
                            field="reference"
                            value={record.reference}
                            className="text-white/90 inline break-all"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'reference'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                        <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                          <span className="font-medium shrink-0">{pt('columns.sku')}:</span>
                          <ProductEditableCell
                            recordId={record.id}
                            field="sku"
                            value={record.sku || record.reference || ''}
                            className="text-white/90 inline font-mono break-all"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'sku'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                        <div className="flex flex-wrap gap-x-1 gap-y-0.5">
                          <span className="font-medium shrink-0">{pt('columns.barcode')}:</span>
                          <ProductEditableCell
                            recordId={record.id}
                            field="barcode"
                            value={record.barcode}
                            className="text-white/85 inline break-all"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'barcode'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                        <div>
                          <span className="font-medium">{pt('columns.category')}:</span>{' '}
                          {getCategoryName(record.category)}
                        </div>
                        {(record.description || editingCell?.recordId === record.id) ? (
                          <div className="pt-1.5 mt-1 border-t border-white/10">
                            <span className="font-medium text-white text-xs">{pt('columns.description')}:</span>
                            <div className="text-white/90 text-xs mt-0.5 line-clamp-2">
                              <ProductEditableCell
                                recordId={record.id}
                                field="description"
                                value={record.description}
                                className="text-white/90 text-xs"
                                isEditing={editingCell?.recordId === record.id && editingCell?.field === 'description'}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onStartEdit={startEditing}
                                onSave={saveInlineEdit}
                                onCancel={cancelEditing}
                              />
                            </div>
                          </div>
                        ) : null}
                      </div>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-white/[0.04] p-3 min-h-0">
                      <h5 className="text-xs font-semibold text-white/90 mb-2 pb-1 border-b border-white/10 flex items-center gap-1.5">
                        <DollarSign size={14} className="text-green-400 shrink-0" />
                        {pt('sections.pricing')}
                      </h5>
                      <div className="space-y-1.5 text-sm text-white/70">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className="font-medium">{pt('columns.price')}:</span>
                          <ProductEditableCell
                            recordId={record.id}
                            field="price"
                            value={record.price != null && record.price !== '' ? String(record.price) : ''}
                            className="text-white inline"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'price'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                          <span className="text-white/60 text-xs">{getBrandCurrencySuffix()}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-1">
                          <span className="font-medium">{pt('labels.purchase_price')}:</span>
                          <ProductEditableCell
                            recordId={record.id}
                            field="cost_price"
                            value={record.cost_price != null && record.cost_price !== '' ? String(record.cost_price) : ''}
                            className="text-white/90 inline"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'cost_price'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                          <span className="text-white/60 text-xs">{getBrandCurrencySuffix()}</span>
                        </div>
                        <div>
                          <span className="font-medium">{pt('columns.unit')}:</span>{' '}
                          <ProductEditableCell
                            recordId={record.id}
                            field="unit"
                            value={record.unit || ''}
                            displayValue={getUnitLabel(record.unit)}
                            className="text-white inline"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'unit'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                        {record.price && record.cost_price ? (
                          <div className="pt-1.5 mt-1 border-t border-white/10 text-xs">
                            <span className="font-medium">{pt('labels.margin')}:</span>{' '}
                            <span className="text-white/90">
                              {formatBrandMoney(parseFloat(record.price) - parseFloat(record.cost_price))}
                            </span>
                          </div>
                        ) : null}
                      </div>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-white/[0.04] p-3 min-h-0">
                      <h5 className="text-xs font-semibold text-white/90 mb-2 pb-1 border-b border-white/10">
                        {pt('sections.stock')}
                      </h5>
                      <div
                        className={`rounded-lg border p-2.5 ${
                          parseFloat(record.stock) === 0
                            ? 'bg-red-500/10 border-red-500/30'
                            : parseFloat(record.stock) <= parseFloat(record.min_stock)
                              ? 'bg-yellow-500/10 border-yellow-500/30'
                              : 'bg-green-500/10 border-green-500/30'
                        }`}
                      >
                        <span className="font-medium text-xs text-white/80">{pt('labels.current_stock')}:</span>
                        <div
                          className={`text-2xl font-bold leading-tight mt-0.5 ${
                            parseFloat(record.stock) === 0
                              ? 'text-red-400'
                              : parseFloat(record.stock) <= parseFloat(record.min_stock)
                                ? 'text-yellow-400'
                                : 'text-green-400'
                          }`}
                        >
                          <ProductEditableCell
                            recordId={record.id}
                            field="stock"
                            value={record.stock != null && record.stock !== '' ? String(record.stock) : ''}
                            className="text-2xl font-bold inline"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'stock'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                        <div className="text-xs mt-1.5 text-white/65">
                          {pt('labels.threshold')}{' '}
                          <ProductEditableCell
                            recordId={record.id}
                            field="min_stock"
                            value={record.min_stock != null && record.min_stock !== '' ? String(record.min_stock) : ''}
                            className="text-xs text-white/85 inline"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'min_stock'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                      </div>
                    </section>

                    <section className="rounded-xl border border-white/10 bg-white/[0.04] p-3 min-h-0 flex flex-col">
                      <h5 className="text-xs font-semibold text-white/90 mb-2 pb-1 border-b border-white/10">
                        {pt('sections.notes_other')}
                      </h5>
                      <div className="flex-1 text-sm text-white/70 min-h-[4.5rem]">
                        <span className="font-medium text-white/75 text-xs">{pt('labels.notes')}:</span>
                        <div className="text-white/65 text-xs mt-1 line-clamp-4">
                          <ProductEditableCell
                            recordId={record.id}
                            field="notes"
                            value={record.notes}
                            className="text-white/70 text-xs"
                            isEditing={editingCell?.recordId === record.id && editingCell?.field === 'notes'}
                            editValue={editValue}
                            onEditValueChange={setEditValue}
                            onStartEdit={startEditing}
                            onSave={saveInlineEdit}
                            onCancel={cancelEditing}
                          />
                        </div>
                      </div>
                    </section>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{pt('empty.no_products')}</p>
              </div>
            )}
          </div>
        )}

        <FacturationListPaginationBar
          totalItems={facturationListTotal}
          rawPage={facturationListPage}
          onPageChange={setFacturationListPage}
          pageSize={facturationPageSize}
        />
                        </div>

      {/* View Modal - Shows 100% of data - WIDE LAYOUT */}
      {showViewModal && viewingRecord && ReactDOM.createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={() => setShowViewModal(false)}
            role="presentation"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className={`backdrop-blur-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`}
              style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '95%', maxWidth: '1100px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{pt('modal.view_title')}</h2>
                <button onClick={() => setShowViewModal(false)} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
                        </div>

              {/* Product image hero — shown first when opening view */}
              <div className={`rounded-xl p-5 border mb-4 ${isDashboardLightMode ? 'bg-gray-50 border-gray-200' : 'bg-white/5 border-white/20'}`}>
                <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-start">
                  <div className="shrink-0">
                    <ProductTablePhoto
                      imageUrl={viewingRecord.image_url}
                      isLight={isDashboardLightMode}
                      sizeClass="h-44 w-44 sm:h-52 sm:w-52"
                      onPreview={viewingRecord.image_url ? () => openProductImagePreview(
                        viewingRecord.image_url,
                        viewingRecord.name_ar || viewingRecord.name_en || viewingRecord.reference,
                      ) : undefined}
                      previewLabel={pt('actions.view_image', { defaultValue: 'عرض الصورة بالكامل' })}
                    />
                  </div>
                  <div className="min-w-0 flex-1 text-center sm:text-start">
                    {viewingRecord.name_ar ? (
                      <p className={`text-2xl font-bold leading-snug ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`} dir="rtl">
                        {viewingRecord.name_ar}
                      </p>
                    ) : null}
                    {viewingRecord.name_en || viewingRecord.name ? (
                      <p className={`${viewingRecord.name_ar ? 'mt-1' : ''} text-lg font-semibold ${isDashboardLightMode ? 'text-gray-700' : 'text-white/90'}`}>
                        {viewingRecord.name_en || viewingRecord.name}
                      </p>
                    ) : null}
                    <div className={`mt-3 flex flex-wrap items-center justify-center gap-3 sm:justify-start ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
                      {viewingRecord.reference ? (
                        <span className="font-mono text-sm">
                          {pt('columns.reference')}: <span className={isDashboardLightMode ? 'text-gray-900 font-medium' : 'text-white font-medium'}>{viewingRecord.reference}</span>
                        </span>
                      ) : null}
                      {viewingRecord.sku ? (
                        <span className="font-mono text-sm">
                          {pt('columns.sku')}: <span className={isDashboardLightMode ? 'text-gray-900 font-medium' : 'text-white font-medium'}>{viewingRecord.sku}</span>
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-3 flex justify-center sm:justify-start">
                      <ClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} />
                    </div>
                    {viewingRecord.image_url ? (
                      <p className={`mt-3 text-xs ${isEmbeddedProductImage(viewingRecord.image_url) ? (isDashboardLightMode ? 'text-emerald-700' : 'text-emerald-400/90') : (isDashboardLightMode ? 'text-gray-500' : 'text-white/50')}`}>
                        {isEmbeddedProductImage(viewingRecord.image_url)
                          ? pt('hints.image_uploaded_locally', { defaultValue: 'تم رفع الصورة من جهازك' })
                          : pt('actions.view_image', { defaultValue: 'اضغط على الصورة للمعاينة بالكامل' })}
                      </p>
                    ) : (
                      <p className={`mt-3 text-sm ${isDashboardLightMode ? 'text-gray-400' : 'text-white/40'}`}>
                        {pt('labels.no_product_image', { defaultValue: 'لا توجد صورة للمنتج' })}
                      </p>
                    )}
                  </div>
                </div>
              </div>
                        
              <div className="space-y-4">
                {/* Informations de Base */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Package size={18} className="text-blue-400" />
                    {pt('sections.basic_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.name_en')}</p>
                      <div className="text-white font-medium text-lg"><ProductEditableCell recordId={viewingRecord.id} field="name_en" value={viewingRecord.name_en || viewingRecord.name} className="text-white font-medium text-lg"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'name_en'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.name_ar')}</p>
                      <div className="text-white font-medium text-lg" dir="rtl"><ProductEditableCell recordId={viewingRecord.id} field="name_ar" value={viewingRecord.name_ar || ''} className="text-white font-medium text-lg"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'name_ar'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.reference')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="reference" value={viewingRecord.reference} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'reference'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.sku')}</p>
                      <div className="text-white font-medium font-mono"><ProductEditableCell recordId={viewingRecord.id} field="sku" value={viewingRecord.sku || ''} className="text-white font-medium font-mono"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'sku'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.barcode')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="barcode" value={viewingRecord.barcode} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'barcode'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.category')}</p>
                      <p className="text-white font-medium">{getCategoryName(viewingRecord.category)}</p>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('labels.type')}</p>
                      <p className="text-white font-medium">{viewingRecord.type === 'product' ? pt('types.product') : pt('types.service')}</p>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.unit')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="unit" value={viewingRecord.unit || ''} displayValue={getUnitLabel(viewingRecord.unit)} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'unit'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.status')}</p>
                      <div className="flex"><ClickableStatusBadge status={viewingRecord.status} recordId={viewingRecord.id} /></div>
                        </div>
                    <div className="md:col-span-4">
                      <p className="text-white/60 text-sm mb-1">{pt('columns.description')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="description" value={viewingRecord.description} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'description'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.country_of_origin')}</p>
                      <p className="text-white font-medium">{viewingRecord.country_of_origin ? getCountryLabel(viewingRecord.country_of_origin) : '—'}</p>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('columns.customs_classification')}</p>
                      <div className="text-white font-medium font-mono"><ProductEditableCell recordId={viewingRecord.id} field="customs_classification" value={viewingRecord.customs_classification || ''} className="text-white font-medium font-mono"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'customs_classification'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                        </div>
                        </div>
                        
                {/* Tarification */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <DollarSign size={18} className="text-green-400" />
                    {pt('sections.pricing')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('labels.sale_price')}</p>
                      <div className="text-white font-medium text-lg"><ProductEditableCell recordId={viewingRecord.id} field="price" value={viewingRecord.price != null && viewingRecord.price !== '' ? String(viewingRecord.price) : ''} className="text-white font-medium text-lg"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'price'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /> {getBrandCurrencySuffix()}</div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('labels.purchase_price')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="cost_price" value={viewingRecord.cost_price != null && viewingRecord.cost_price !== '' ? String(viewingRecord.cost_price) : ''} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'cost_price'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /> {getBrandCurrencySuffix()}</div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('labels.tax_percent_short')}</p>
                      <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="tax_rate" value={viewingRecord.tax_rate != null && viewingRecord.tax_rate !== '' ? String(viewingRecord.tax_rate) : ''} className="text-white font-medium"
                        isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'tax_rate'}
                        editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                      /></div>
                        </div>
                    <div>
                      <p className="text-white/60 text-sm mb-1">{pt('labels.margin')}</p>
                      <p className="text-white font-medium">
                        {viewingRecord.price && viewingRecord.cost_price ? 
                          formatBrandMoney(parseFloat(viewingRecord.price) - parseFloat(viewingRecord.cost_price)) : 
                          '-'}
                      </p>
                        </div>
                        </div>
                        </div>

                {/* Grid layout for Stock and Notes */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Stock */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <Box size={18} className="text-yellow-400" />
                      {pt('sections.stock')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{pt('labels.current_stock')}</p>
                        <div className={`font-medium text-2xl ${
                          parseFloat(viewingRecord.stock) === 0 ? 'text-red-400' :
                          parseFloat(viewingRecord.stock) <= parseFloat(viewingRecord.min_stock) ? 'text-yellow-400' :
                          'text-green-400'
                        }`}><ProductEditableCell recordId={viewingRecord.id} field="stock" value={viewingRecord.stock != null && viewingRecord.stock !== '' ? String(viewingRecord.stock) : ''} className={`font-medium text-2xl ${
                          parseFloat(viewingRecord.stock) === 0 ? 'text-red-400' :
                          parseFloat(viewingRecord.stock) <= parseFloat(viewingRecord.min_stock) ? 'text-yellow-400' :
                          'text-green-400'
                        }`}
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'stock'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                        </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{pt('labels.min_stock')}</p>
                        <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="min_stock" value={viewingRecord.min_stock != null && viewingRecord.min_stock !== '' ? String(viewingRecord.min_stock) : ''} className="text-white font-medium"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'min_stock'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                      </div>
                      <div>
                        <p className="text-white/60 text-sm mb-1">{pt('labels.max_stock')}</p>
                        <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="max_stock" value={viewingRecord.max_stock != null && viewingRecord.max_stock !== '' ? String(viewingRecord.max_stock) : ''} className="text-white font-medium"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'max_stock'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                      </div>
                        </div>
                        </div>
                        
                  {/* Notes & Autres */}
                  <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                    <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                      <FileText size={18} className="text-purple-400" />
                      {pt('sections.notes_other')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-white/60 text-sm mb-1">{pt('labels.notes')}</p>
                        <div className="text-white font-medium"><ProductEditableCell recordId={viewingRecord.id} field="notes" value={viewingRecord.notes} className="text-white font-medium"
                          isEditing={editingCell?.recordId === viewingRecord.id && editingCell?.field === 'notes'}
                          editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing}
                        /></div>
                        </div>
                      {viewingRecord.created_at && (
                        <div>
                          <p className="text-white/60 text-sm mb-1">{pt('labels.created_at')}</p>
                          <p className="text-white font-medium">{new Date(viewingRecord.created_at).toLocaleString(dateLocale)}</p>
                        </div>
                      )}
                        </div>
                        </div>
                        </div>
                        </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* Edit/Create Modal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div ref={productModalPanelRef} data-product-modal-panel="true" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }} onClick={(e) => e.stopPropagation()}>
            {/* Fixed Header */}
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>{editingRecord ? pt('modal.edit_title') : pt('modal.create_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="product-form" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30">
                  <CheckCircle size={18} />
                  {editingRecord ? pt('modal.update') : pt('modal.create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}><X size={24} /></button>
                        </div>
                        </div>

            <FacturationFormModalTabs
              tabs={productFormTabs}
              activeTab={productFormTab}
              onTabChange={setProductFormTab}
              isLight={isDashboardLightMode}
              ariaLabel={editingRecord ? pt('modal.edit_title') : pt('modal.create_title')}
            />

            {/* Tab panel — short scroll only within active section */}
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="product-form" className={ERP_MODAL_FORM_CLASS} onSubmit={handleSubmit}>
              {productFormTab === 'basic' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={pt('columns.reference')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.reference}
                            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={pt('placeholders.reference')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={pt('columns.sku')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.sku}
                            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={pt('placeholders.sku')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={pt('columns.name_en')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.name_en}
                            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={pt('placeholders.name_en')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            <RequiredLabelText text={pt('columns.name_ar')} required />
                          </label>
                          <input
                            type="text"
                            value={formData.name_ar}
                            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={pt('placeholders.name_ar')}
                            dir="rtl"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.barcode')}</label>
                          <input
                            type="text"
                            value={formData.barcode}
                            onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={pt('placeholders.barcode')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>
                            {articlesConfig.requireCategory ? (
                              <RequiredLabelText text={pt('columns.category')} required />
                            ) : (
                              pt('columns.category')
                            )}
                          </label>
                          <CustomSelect
                            value={formData.category === '' || formData.category == null ? '' : formData.category}
                            onChange={(value) =>
                              setFormData({
                                ...formData,
                                category: value === '__clear__' || value === '' || value == null ? '' : value,
                              })
                            }
                            options={productCategorySelectOptions}
                            placeholder={pt('placeholders.select_category')}
                            searchable
                            creatable={false}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.unit')}</label>
                          <CustomSelect
                            value={formData.unit || ''}
                            onChange={(value) => setFormData({ ...formData, unit: value || '' })}
                            options={productUnitOptions}
                            placeholder={pt('units.select')}
                            searchable
                            creatable={false}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.status')}</label>
                          <CustomSelect
                            value={formData.status}
                            onChange={(value) => setFormData({ ...formData, status: value })}
                            options={productStatusSelectOptions}
                            placeholder={pt('placeholders.select_status')}
                            searchable={false}
                            creatable={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.type')}</label>
                          <CustomSelect
                            value={formData.type}
                            onChange={(value) => setFormData({ ...formData, type: value })}
                            options={productTypeSelectOptions}
                            placeholder={pt('placeholders.select_or_create')}
                            searchable={false}
                            creatable={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.country_of_origin')}</label>
                          <CustomSelect
                            value={formData.country_of_origin || ''}
                            onChange={(value) => setFormData({ ...formData, country_of_origin: value || '' })}
                            options={productCountrySelectOptions}
                            placeholder={pt('countries.select')}
                            searchable
                            creatable={false}
                            autoSelect={false}
                          />
                        </div>
                        <div className="min-w-0">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.customs_classification')}</label>
                          <input
                            type="text"
                            value={formData.customs_classification}
                            onChange={(e) => setFormData({ ...formData, customs_classification: e.target.value })}
                            className="w-full min-h-[2.75rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                            placeholder={pt('placeholders.customs_classification')}
                            dir="ltr"
                          />
                        </div>
                        <div className="min-w-0 md:col-span-3">
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('columns.description')}</label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full min-h-[9.5rem] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none"
                            placeholder={pt('placeholders.description')}
                            rows={4}
                          />
                        </div>
                          <div className="min-w-0 md:col-span-3">
                            <label className={articleFormLabelClass(isDashboardLightMode)}>
                              {pt('labels.product_image', { defaultValue: 'صورة المنتج' })}
                            </label>
                            <div className="flex flex-wrap items-start gap-3">
                              {formData.image_url ? (
                                <div className="relative shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => openProductImagePreview(formData.image_url, formData.name_ar || formData.name_en || formData.reference)}
                                    className="group block rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/70"
                                    title={pt('actions.view_image', { defaultValue: 'عرض الصورة بالكامل' })}
                                    aria-label={pt('actions.view_image', { defaultValue: 'عرض الصورة بالكامل' })}
                                  >
                                    <img
                                      src={formData.image_url}
                                      alt=""
                                      className="h-24 w-24 rounded-xl object-cover border border-white/20 bg-white/5 transition-transform group-hover:scale-[1.02]"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setFormData({ ...formData, image_url: '' })}
                                    className="absolute -top-2 -end-2 p-1 rounded-full bg-red-500 text-white shadow-md hover:bg-red-600"
                                    title={pt('actions.remove_image', { defaultValue: 'إزالة الصورة' })}
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ) : (
                                <div className={`h-24 w-24 rounded-xl border border-dashed flex items-center justify-center shrink-0 ${isDashboardLightMode ? 'border-gray-300 bg-gray-50' : 'border-white/25 bg-white/5'}`}>
                                  <ImagePlus size={28} className={isDashboardLightMode ? 'text-gray-400' : 'text-white/30'} />
                                </div>
                              )}
                              <div className="flex flex-col gap-2 flex-1 min-w-0">
                                <input
                                  ref={productImageInputRef}
                                  type="file"
                                  accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                                  className="hidden"
                                  onChange={handleProductImageFileChange}
                                />
                                <div className="flex flex-wrap items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => productImageInputRef.current?.click()}
                                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors shrink-0 ${isDashboardLightMode ? 'border-[#a67c3d]/45 bg-[#faf8f4] text-[#704820] hover:bg-[#f3ebe0]' : 'border-white/20 bg-white/10 text-white hover:bg-white/15'}`}
                                  >
                                    <Upload size={16} />
                                    {pt('actions.upload_from_pc', { defaultValue: 'رفع من الجهاز' })}
                                  </button>
                                  <input
                                    type="text"
                                    value={formData.image_url?.startsWith('data:') ? '' : (formData.image_url || '')}
                                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                                    className="flex-1 min-w-[140px] bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm"
                                    placeholder={pt('placeholders.image_url')}
                                    disabled={Boolean(formData.image_url?.startsWith('data:'))}
                                  />
                                </div>
                                <p className={`text-xs ${isDashboardLightMode ? 'text-gray-500' : 'text-white/45'}`}>
                                  {pt('hints.image_upload', { maxMb: MAX_PRODUCT_IMAGE_MB, defaultValue: `JPG, PNG, GIF, WebP — حتى ${MAX_PRODUCT_IMAGE_MB} ميجابايت` })}
                                </p>
                                {formData.image_url?.startsWith('data:') ? (
                                  <p className={`text-xs ${isDashboardLightMode ? 'text-emerald-700' : 'text-emerald-400'}`}>
                                    {pt('hints.image_uploaded_locally', { defaultValue: 'تم رفع الصورة من جهازك — احفظ المنتج لتطبيقها' })}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                          </div>
              </div>
              )}

              {productFormTab === 'pricing_stock' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt(qawaem ? 'labels.sale_price_sar' : 'labels.sale_price_da')}</label>
                          <CustomNumberInput
                            value={formData.price}
                            onChange={(value) => setFormData({ ...formData, price: value })}
                            placeholder={pt('placeholders.price')}
                            step={0.01}
                            min={0}
                            decimals={2}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt(qawaem ? 'labels.purchase_price_sar' : 'labels.purchase_price_da')}</label>
                          <CustomNumberInput
                            value={formData.cost_price}
                            onChange={(value) => setFormData({ ...formData, cost_price: value })}
                            placeholder={pt('placeholders.cost_price')}
                            step={0.01}
                            min={0}
                            decimals={2}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.tax_percent', { rate: liveVatRate })}</label>
                          <CustomNumberInput
                            value={formData.tax_rate}
                            onChange={(value) => setFormData({ ...formData, tax_rate: value })}
                            placeholder={pt('placeholders.tax_rate')}
                            step={0.1}
                            min={0}
                            max={100}
                            decimals={1}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.notes')}</label>
                          <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-sm resize-none" placeholder={pt('placeholders.notes')} rows={2} />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.current_stock')}</label>
                          <CustomNumberInput
                            value={formData.stock}
                            onChange={(value) => setFormData({ ...formData, stock: value })}
                            placeholder={pt('placeholders.stock')}
                            step={0.1}
                            min={0}
                            decimals={1}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.min_stock')}</label>
                          <CustomNumberInput
                            value={formData.min_stock}
                            onChange={(value) => setFormData({ ...formData, min_stock: value })}
                            placeholder={pt('placeholders.min_stock')}
                            step={0.1}
                            min={0}
                            decimals={1}
                          />
                        </div>
                        <div>
                          <label className={articleFormLabelClass(isDashboardLightMode)}>{pt('labels.max_stock_form')}</label>
                          <CustomNumberInput
                            value={formData.max_stock}
                            onChange={(value) => setFormData({ ...formData, max_stock: value })}
                            placeholder={pt('placeholders.max_stock')}
                            step={0.1}
                            min={0}
                            decimals={1}
                          />
                        </div>
              </div>
              )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      {imagePreview && ReactDOM.createPortal(
        <div
          className={QAWAEM_MODAL_OVERLAY_CLASS}
          onClick={closeProductImagePreview}
          role="dialog"
          aria-modal="true"
          aria-label={pt('modal.image_preview_title', { defaultValue: 'معاينة صورة المنتج' })}
        >
          <div
            className={`relative max-w-5xl w-full max-h-[92vh] overflow-auto rounded-2xl border p-4 ${
              isDashboardLightMode
                ? 'bg-white border-slate-200 shadow-xl'
                : 'border-white/20 bg-gradient-to-br from-slate-900/95 via-slate-900/90 to-blue-950/40 backdrop-blur-lg'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeProductImagePreview}
              className="absolute top-4 end-4 z-10 rounded-full bg-red-500/80 p-2 text-white transition-colors hover:bg-red-500"
              aria-label={pt('actions.close_image_preview', { defaultValue: 'إغلاق' })}
            >
              <X className="h-5 w-5" />
            </button>
            {imagePreview.title ? (
              <p className={`mb-3 pe-12 text-sm font-semibold ${isDashboardLightMode ? 'text-slate-800' : 'text-white/90'}`}>
                {imagePreview.title}
              </p>
            ) : null}
            <img
              src={imagePreview.url}
              alt={imagePreview.title || pt('modal.image_preview_title', { defaultValue: 'معاينة صورة المنتج' })}
              className="mx-auto max-h-[78vh] w-auto max-w-full rounded-xl object-contain"
            />
          </div>
        </div>,
        document.body,
      )}
      <AppConfirmDialog
        open={deleteConfirm.open}
        title={
          deleteConfirm.ids.length > 1
            ? t('common.delete', 'حذف')
            : `${t('common.delete', 'حذف')} — ${pt('entity_label', { defaultValue: 'المنتج' })}`
        }
        message={
          deleteConfirm.ids.length > 1
            ? pt('alerts.bulk_delete_confirm', { count: deleteConfirm.ids.length })
            : pt('alerts.delete_confirm')
        }
        confirmLabel={t('common.delete', 'حذف')}
        cancelLabel={t('common.cancel', 'إلغاء')}
        danger
        onCancel={closeDeleteConfirm}
        onConfirm={executeDeleteProducts}
      />
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default Products; 


